import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArrowDown, ArrowUp, CalendarClock, CalendarDays, CalendarPlus, Check, CheckCircle2, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Clock, Copy, Download, FileSearch, FileText, Filter, MessageSquare, PackageOpen, Search, UserRound } from 'lucide-react';
import { api, ErroHttp } from '@/lib/api';
import { useSessao } from '@/hooks/useSessao';
import { cn, compararNatural, nomeFornecedor } from '@/lib/utils';
import { FiltroDeColuna } from '@/componentes/tabela/FiltroDeColuna';
import { baixarCsv, carimboArquivo } from '@/lib/csv';
import { acaoTexto, Botao } from '@/componentes/ui/botao';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { Cartao, Esqueleto, Pilula } from '@/componentes/ui/primitivos';
import { type Destino, FASES, type FaseRecebimento, haQuanto, moeda, type Etapa, type Nota, type OcPrePedido, type RespostaEsteira, type Status, type TipoOcPre, type ValorOc, precosDaMensagem, SUBETAPAS_RECEBIMENTO, textoJaTransferida, textoProgresso, TIPO_DIVERGENCIA } from './tipos';
import { EscolherMotivo } from './Dispensar';

/**
 * A esteira: onde cada nota está e de quem é a vez.
 *
 * O QUE ESTA TELA EVITA
 * ---------------------
 * A conciliação do portal fiscal mostra tudo para todos, e quem abre não sabe
 * por onde começar (queixa do Paulo, 27/10/2026). Aqui:
 *  - abre em "O que é meu": só as etapas que a pessoa pode resolver, com nota;
 *  - as etapas vêm na ordem do fluxo, em quatro grupos, e etapa vazia apaga;
 *  - cada linha diz há quanto tempo está parada e, numa frase, o porquê;
 *  - a ação mora na gaveta, um botão só por etapa. A lista não tem botão.
 */

/** `fase:…` = "Em conferência" numa fase da cega (o card do Recebimento aberto). */
type Filtro = 'meu' | 'todas' | Status | `fase:${FaseRecebimento}`;
type Time = NonNullable<Etapa['dono']>;

/** Os times donos das etapas, na ordem do fluxo. */
/*
 * Uma cor por time, só na faixa e no nome: âmbar fica para "parado" e
 * vermelho para divergência, como no resto da tela.
 */
const TIMES: { time: Time; rotulo: string; faixa: string; texto: string; ativo: string }[] = [
  { time: 'comprador', rotulo: 'Compras', faixa: 'bg-sky-500', texto: 'text-sky-700 dark:text-sky-300', ativo: 'bg-sky-50 dark:bg-sky-950/40' },
  { time: 'fiscal', rotulo: 'Fiscal', faixa: 'bg-violet-500', texto: 'text-violet-700 dark:text-violet-300', ativo: 'bg-violet-50 dark:bg-violet-950/40' },
  { time: 'logistica', rotulo: 'Logística', faixa: 'bg-teal-500', texto: 'text-teal-700 dark:text-teal-300', ativo: 'bg-teal-50 dark:bg-teal-950/40' },
  { time: 'cd', rotulo: 'CD', faixa: 'bg-indigo-500', texto: 'text-indigo-700 dark:text-indigo-300', ativo: 'bg-indigo-50 dark:bg-indigo-950/40' },
];

export function Esteira({
  aoAbrir,
  aoAgendarLote,
}: {
  aoAbrir: (chave: string) => void;
  aoAgendarLote: (chaves: string[]) => void;
}): JSX.Element {
  const { temPermissao } = useSessao();
  const [filtro, definirFiltro] = React.useState<Filtro>('meu');
  const [busca, definirBusca] = React.useState('');
  const [buscaAplicada, definirBuscaAplicada] = React.useState('');
  const [marcadas, definirMarcadas] = React.useState<Set<string>>(new Set());
  // "Emitidas há mais de 15 dias": o que sobrou (a primeira volta trouxe 7 dias
  // de notas de uma vez), para dispensar em lote o que já foi tratado à mão.
  const [soAntigas, definirSoAntigas] = React.useState(false);
  const [ordem, definirOrdem] = React.useState<Ordem>('parada');
  // Período de emissão: por padrão, os últimos 30 dias (pedido do Paulo, 28/10/2026).
  const [de, definirDe] = React.useState(() => diaIso(-30));
  const [ate, definirAte] = React.useState(() => diaIso(0));
  // SKU (produto do PBS, código do fornecedor ou EAN), destino, time e comprador.
  const [sku, definirSku] = React.useState('');
  const [skuAplicado, definirSkuAplicado] = React.useState('');
  const [destinoId, definirDestinoId] = React.useState('');
  const [time, definirTime] = React.useState<Time | null>(null);
  const [comprador, definirComprador] = React.useState('');
  React.useEffect(() => {
    const t = setTimeout(() => definirSkuAplicado(sku.trim()), 350);
    return () => clearTimeout(t);
  }, [sku]);
  const destinos = useQuery<{ destinos: Destino[] }>({ queryKey: ['entradas-destinos'], queryFn: () => api.get('/api/entradas/destinos') });

  React.useEffect(() => {
    const t = setTimeout(() => definirBuscaAplicada(busca.trim()), 350);
    return () => clearTimeout(t);
  }, [busca]);

  const faseFiltro = filtro.startsWith('fase:') ? (filtro.slice(5) as FaseRecebimento) : null;
  const subetapaFiltro = faseFiltro ? SUBETAPAS_RECEBIMENTO.find((s) => s.fase === faseFiltro) : undefined;
  const statusDaConsulta: Status | null =
    filtro === 'meu' || filtro === 'todas' ? null : faseFiltro ? 'em_conferencia' : (filtro as Status);
  const consulta = useQuery<RespostaEsteira>({
    queryKey: ['entradas', statusDaConsulta, buscaAplicada, de, ate, skuAplicado, destinoId],
    queryFn: () =>
      api.get<RespostaEsteira>('/api/entradas', {
        status: statusDaConsulta,
        busca: buscaAplicada || null,
        de: de || null,
        ate: ate || null,
        sku: skuAplicado || null,
        destino: destinoId || null,
      }),
    refetchInterval: 60_000,
  });

  const etapas = React.useMemo(
    () => new Map((consulta.data?.etapas ?? []).map((e) => [e.status, e])),
    [consulta.data],
  );
  const contagem = React.useMemo(
    () => new Map((consulta.data?.contagens ?? []).map((c) => [c.status, c])),
    [consulta.data],
  );
  const minhas = React.useMemo(
    () =>
      new Set(
        (consulta.data?.etapas ?? [])
          .filter((e) => e.permissao && temPermissao(e.permissao) && !e.final)
          .map((e) => e.status),
      ),
    [consulta.data, temPermissao],
  );

  const limiteAntigas = Date.now() - 15 * 86_400_000;
  const ehAntiga = (n: Nota): boolean => n.emissao != null && new Date(n.emissao).getTime() < limiteAntigas;
  const doTime = (n: Nota): boolean => !time || etapas.get(n.status)?.dono === time;
  const notasBase = ordenar(
    (consulta.data?.notas ?? [])
      .filter((n) => filtro !== 'meu' || Boolean(buscaAplicada) || Boolean(time) || minhas.has(n.status))
      .filter((n) => !soAntigas || ehAntiga(n))
      .filter((n) => !faseFiltro || n.recebimentoProgresso?.fase === faseFiltro)
      .filter(doTime)
      .filter((n) => !comprador || n.compradorNome === comprador),
    ordem,
  );
  // Filtro e ordem por coluna, como no Excel (pedido do Paulo, 28/10/2026).
  const ordemEtapa = new Map(FASES.flatMap((f) => f.status).map((st, i) => [st, i]));
  const tempo = (v: string | null): number => (v ? new Date(v).getTime() : 0);
  const colunas: ColunaEsteira[] = [
    {
      chave: 'fornecedor',
      cabecalho: 'Fornecedor e nota',
      span: 'sm:col-span-4',
      filtrar: (n) => nomeFornecedor(n.emitenteNome, n.emitenteUf) ?? n.emitenteCnpj ?? '',
      comparar: (a, b) => compararNatural(nomeFornecedor(a.emitenteNome, a.emitenteUf) ?? '', nomeFornecedor(b.emitenteNome, b.emitenteUf) ?? ''),
    },
    {
      chave: 'etapa',
      cabecalho: 'Etapa',
      span: 'sm:col-span-2',
      filtrar: (n) => etapas.get(n.status)?.rotulo ?? n.status,
      comparar: (a, b) => (ordemEtapa.get(a.status) ?? 99) - (ordemEtapa.get(b.status) ?? 99),
    },
    {
      chave: 'dono',
      cabecalho: 'Parado com',
      span: 'sm:col-span-2',
      filtrar: (n) => {
        const e = etapas.get(n.status);
        return (e && !e.final && TIMES.find((x) => x.time === e.dono)?.rotulo) || '—';
      },
      comparar: (a, b) =>
        TIMES.findIndex((x) => x.time === etapas.get(a.status)?.dono) - TIMES.findIndex((x) => x.time === etapas.get(b.status)?.dono),
    },
    {
      chave: 'destino',
      cabecalho: 'O que falta / destino',
      rotuloFiltro: 'Destino',
      span: 'sm:col-span-2',
      filtrar: (n) => n.destinoNome ?? n.preDestino?.texto.replace(/\s*\(.*\).*$/, '') ?? '(sem destino)',
      comparar: (a, b) => compararNatural(a.destinoNome ?? '', b.destinoNome ?? ''),
    },
    {
      chave: 'idade',
      cabecalho: 'Idade · na etapa',
      span: 'sm:col-span-2',
      direita: true,
      filtrar: (n) => {
        const d = n.emissao ? (Date.now() - tempo(n.emissao)) / 86_400_000 : null;
        return d == null ? '(sem emissão)' : d > 15 ? 'NF com +15 dias' : d > 7 ? 'NF de 8 a 15 dias' : 'NF de até 7 dias';
      },
      // Crescente = a NF mais velha primeiro.
      comparar: (a, b) => tempo(a.emissao) - tempo(b.emissao),
    },
  ];
  const [filtrosCol, definirFiltrosCol] = React.useState<Record<string, string[] | null>>({});
  const [ordemCol, definirOrdemCol] = React.useState<{ chave: string; desc: boolean } | null>(null);
  const [filtroAberto, definirFiltroAberto] = React.useState<{ chave: string; ancora: DOMRect } | null>(null);
  const passaFiltros = (n: Nota, menos?: string): boolean =>
    colunas.every((c) => c.chave === menos || !filtrosCol[c.chave] || filtrosCol[c.chave]!.includes(c.filtrar(n)));
  const notas = (() => {
    const filtradas = notasBase.filter((n) => passaFiltros(n));
    const col = ordemCol ? colunas.find((c) => c.chave === ordemCol.chave) : undefined;
    if (!col) return filtradas;
    return [...filtradas].sort((a, b) => (ordemCol!.desc ? -1 : 1) * col.comparar(a, b));
  })();
  // Por OC: uma caixa por OC com as notas dela (a mesma OC vem em várias notas espalhadas);
  // sem OC, uma caixa por fornecedor. "Por fornecedor": fornecedor → OCs dele → notas.
  // "Lista" volta à lista corrida.
  const [agrupar, definirAgrupar] = React.useState<Agrupar>(() => {
    try {
      const v = localStorage.getItem('wms.entradas.agrupar');
      return v === 'lista' || v === 'fornecedor' ? v : 'oc';
    } catch {
      return 'oc';
    }
  });
  const trocarAgrupar = (v: Agrupar): void => {
    definirAgrupar(v);
    try {
      localStorage.setItem('wms.entradas.agrupar', v);
    } catch {
      /* sem armazenamento: só não lembra */
    }
  };
  const compradores = [...new Set((consulta.data?.notas ?? []).map((n) => n.compradorNome).filter((c): c is string => Boolean(c)))].sort();
  const nomeDestino = (id: string): string => {
    const d = destinos.data?.destinos.find((x) => String(x.id) === id);
    return d ? `${d.nome} ${d.uf}` : `destino ${id}`;
  };
  // Pendências por time: o que está em aberto em etapa de cada um, quanto
  // está parado há +24 h e desde quando a mais antiga espera.
  const porTime = TIMES.map(({ time: t, rotulo, ...cores }) => {
    const sts = (consulta.data?.etapas ?? []).filter((e) => e.dono === t && !e.final).map((e) => e.status);
    const cs = sts.map((st) => contagem.get(st)).filter((c): c is NonNullable<typeof c> => Boolean(c));
    const maisAntigo = cs.map((c) => c.maisAntigo).filter((x): x is string => Boolean(x)).sort()[0] ?? null;
    // "Parada" conta da entrada na ETAPA, e o WMS começou em 27/10/2026: notas
    // emitidas há dias aparecem como recentes. A idade da NF mostra o atraso real.
    const limiteEmissao = Date.now() - 7 * 86_400_000;
    const velhas = (consulta.data?.notas ?? []).filter(
      (n) => sts.includes(n.status) && n.emissao != null && new Date(n.emissao).getTime() < limiteEmissao,
    ).length;
    const etapasDoTime = sts
      .map((st) => ({ rotulo: etapas.get(st)?.rotulo ?? st, total: contagem.get(st)?.total ?? 0 }))
      .filter((x) => x.total > 0);
    return {
      etapasDoTime,
      velhas,
      time: t,
      rotulo,
      ...cores,
      total: cs.reduce((a, c) => a + c.total, 0),
      parados: cs.reduce((a, c) => a + c.parados, 0),
      maisAntigo,
    };
  });
  const totalAberto = porTime.reduce((a, t) => a + t.total, 0);
  const exportar = (): void =>
    baixarCsv(`entradas-${carimboArquivo()}`, notas, [
      { cabecalho: 'Chave', valor: (n) => n.chave, texto: true },
      { cabecalho: 'NF', valor: (n) => n.numero },
      { cabecalho: 'Série', valor: (n) => n.serie },
      { cabecalho: 'Emissão', valor: (n) => (n.emissao ? new Date(n.emissao).toLocaleDateString('pt-BR') : '') },
      { cabecalho: 'Fornecedor', valor: (n) => nomeFornecedor(n.emitenteNome, n.emitenteUf) },
      { cabecalho: 'CNPJ', valor: (n) => n.emitenteCnpj, texto: true },
      { cabecalho: 'Valor', valor: (n) => (n.valor == null ? '' : n.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })) },
      { cabecalho: 'OC', valor: (n) => n.pedidoCompra },
      { cabecalho: 'Comprador', valor: (n) => n.compradorNome },
      { cabecalho: 'Etapa', valor: (n) => etapas.get(n.status)?.rotulo ?? n.status },
      { cabecalho: 'Na etapa desde', valor: (n) => new Date(n.statusDesde).toLocaleString('pt-BR') },
      { cabecalho: 'O que falta / motivo', valor: (n) => n.motivo },
      { cabecalho: 'Destino', valor: (n) => n.destinoNome },
      { cabecalho: 'Agendada para', valor: (n) => (n.agendaData ? n.agendaData.split('-').reverse().join('/') : '') },
      { cabecalho: 'NF de compra (ERP)', valor: (n) => n.nfCompra },
      { cabecalho: 'Cega', valor: (n) => n.recebimento },
      {
        cabecalho: 'Fase da cega',
        valor: (n) => (n.recebimentoProgresso ? SUBETAPAS_RECEBIMENTO.find((s) => s.fase === n.recebimentoProgresso!.fase)?.rotulo ?? '' : ''),
      },
      {
        cabecalho: 'Volumes alocados',
        valor: (n) => (n.recebimentoProgresso?.etiquetas ? `${n.recebimentoProgresso.alocadas}/${n.recebimentoProgresso.etiquetas}` : ''),
        texto: true,
      },
    ]);
  const filtrosAtivos: { rotulo: string; tirar: () => void }[] = [
    ...(statusDaConsulta
      ? [{
          rotulo: subetapaFiltro ? `Recebimento › ${subetapaFiltro.rotulo}` : etapas.get(statusDaConsulta)?.rotulo ?? statusDaConsulta,
          tirar: () => definirFiltro('todas'),
        }]
      : []),
    ...(buscaAplicada ? [{ rotulo: `“${buscaAplicada}”`, tirar: () => definirBusca('') }] : []),
    ...(soAntigas ? [{ rotulo: 'emitidas há +15 dias', tirar: () => definirSoAntigas(false) }] : []),
    ...(buscaAplicada && (de || ate) ? [{ rotulo: 'a busca ignora o período', tirar: () => definirBusca('') }] : []),
    ...(skuAplicado ? [{ rotulo: `SKU ${skuAplicado}`, tirar: () => definirSku('') }] : []),
    ...(destinoId ? [{ rotulo: `→ ${nomeDestino(destinoId)}`, tirar: () => definirDestinoId('') }] : []),
    ...(time ? [{ rotulo: `com ${TIMES.find((x) => x.time === time)?.rotulo ?? time}`, tirar: () => definirTime(null) }] : []),
    ...(comprador ? [{ rotulo: `comprador ${comprador}`, tirar: () => definirComprador('') }] : []),
    ...colunas
      .filter((c) => filtrosCol[c.chave])
      .map((c) => ({
        rotulo: `${c.rotuloFiltro ?? c.cabecalho}: ${filtrosCol[c.chave]!.length === 1 ? filtrosCol[c.chave]![0] : `${filtrosCol[c.chave]!.length} valores`}`,
        tirar: () => definirFiltrosCol((f) => ({ ...f, [c.chave]: null })),
      })),
  ];
  const totalMeu = [...minhas].reduce((s, st) => s + (contagem.get(st)?.total ?? 0), 0);
  const podeAgendar = temPermissao('entradas.agendar');
  // Dispensar: quem age na etapa da nota, ou o fiscal (a mesma regra da API).
  const ehFiscal = temPermissao('entradas.fiscal');
  const podeDispensarNota = (n: Nota): boolean => {
    const e = etapas.get(n.status);
    return Boolean(e && !e.final && (ehFiscal || (e.permissao && temPermissao(e.permissao))));
  };
  const marcavel = (n: Nota): boolean => (podeAgendar && n.status === 'aguardando_agendamento') || podeDispensarNota(n);
  const marcaveis = notas.filter(marcavel);
  const selecionadas = notas.filter((n) => marcadas.has(n.chave));
  const todasAgendaveis = selecionadas.length > 0 && selecionadas.every((n) => n.status === 'aguardando_agendamento');
  const [dispensando, definirDispensando] = React.useState(false);
  const cliente = useQueryClient();
  const dispensar = useMutation({
    mutationFn: (motivo: string) =>
      api.post<{ feitas: number; recusadas: { chave: string; motivo: string }[] }>('/api/entradas/dispensar', { chaves: [...marcadas], motivo }),
    onSuccess: (r) => {
      definirDispensando(false);
      definirMarcadas(new Set(r.recusadas.map((x) => x.chave)));
      void cliente.invalidateQueries({ queryKey: ['entradas'] });
    },
  });

  const alternar = (chave: string): void =>
    definirMarcadas((m) => {
      const novo = new Set(m);
      if (novo.has(chave)) novo.delete(chave);
      else novo.add(chave);
      return novo;
    });

  if (consulta.isError) return <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />;

  return (
    <div className="space-y-4">
      <Cartao className="overflow-hidden">
        {/* -------- busca e ações -------- */}
        <div className="flex flex-wrap items-center gap-2 border-b border-borda p-3">
          <div className="inline-flex rounded-lg bg-superficie2 p-0.5 ring-1 ring-inset ring-borda">
            {(
              [
                ['meu', <>O que é meu <b className="tabular">{totalMeu}</b></>],
                ['todas', 'Todas em aberto'],
              ] as const
            ).map(([v, rotulo]) => (
              <button
                key={v}
                type="button"
                onClick={() => definirFiltro(v)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition',
                  filtro === v ? 'bg-superficie text-texto shadow-xs' : 'text-textoFraco hover:text-texto',
                )}
              >
                {v === 'meu' ? <UserRound className="h-3.5 w-3.5" /> : null}
                {rotulo}
              </button>
            ))}
          </div>
          <div className="relative min-w-[16rem] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-textoSuave" />
            <input
              className="h-9 w-full rounded-lg border border-borda bg-superficie pl-9 pr-8 text-sm shadow-xs placeholder:text-textoSuave focus:border-primaria focus:outline-none focus:ring-4 focus:ring-primaria/12"
              placeholder="Buscar por NF, OC, fornecedor, CNPJ, chave ou nº da cega"
              value={busca}
              onChange={(e) => definirBusca(e.target.value)}
            />
            {busca ? (
              <button
                type="button"
                aria-label="Limpar busca"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-1.5 text-textoSuave hover:text-texto"
                onClick={() => definirBusca('')}
              >
                ×
              </button>
            ) : null}
          </div>
          <div className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-borda bg-superficie px-2 text-xs text-textoFraco" title="Emissão da NF">
            <CalendarDays className="h-3.5 w-3.5" />
            <input
              type="date"
              aria-label="Emissão de"
              className="bg-transparent text-xs font-semibold text-texto focus:outline-none"
              value={de}
              max={ate || undefined}
              onChange={(e) => definirDe(e.target.value)}
            />
            <span>até</span>
            <input
              type="date"
              aria-label="Emissão até"
              className="bg-transparent text-xs font-semibold text-texto focus:outline-none"
              value={ate}
              min={de || undefined}
              onChange={(e) => definirAte(e.target.value)}
            />
          </div>
          <div className="relative w-36">
            <input
              className="h-9 w-full rounded-lg border border-borda bg-superficie px-3 text-xs shadow-xs placeholder:text-textoSuave focus:border-primaria focus:outline-none focus:ring-4 focus:ring-primaria/12"
              placeholder="SKU, cód. ou EAN"
              aria-label="Filtrar por SKU"
              title="Produto do PBS, código do fornecedor ou EAN de algum item da nota"
              value={sku}
              onChange={(e) => definirSku(e.target.value)}
            />
          </div>
          <select
            className={cn(
              'h-9 rounded-lg border bg-superficie px-2 text-xs font-semibold',
              time ? 'border-primaria text-primaria' : 'border-borda text-textoFraco',
            )}
            value={time ?? ''}
            onChange={(e) => definirTime((e.target.value || null) as Time | null)}
            aria-label="Pendente com"
          >
            <option value="">Pendente com: todos</option>
            {porTime.map((t) => (
              <option key={t.time} value={t.time}>
                Pendente com {t.rotulo} ({t.total}{t.parados ? `, ${t.parados} parada(s)` : ''})
              </option>
            ))}
          </select>
          <select
            className="h-9 max-w-[12rem] rounded-lg border border-borda bg-superficie px-2 text-xs font-semibold text-textoFraco"
            value={destinoId}
            onChange={(e) => definirDestinoId(e.target.value)}
            aria-label="Destino"
          >
            <option value="">Todos os destinos</option>
            {(destinos.data?.destinos ?? []).map((d) => (
              <option key={d.id} value={String(d.id)}>
                {d.nome} {d.uf}
              </option>
            ))}
          </select>
          {compradores.length ? (
            <select
              className="h-9 max-w-[12rem] rounded-lg border border-borda bg-superficie px-2 text-xs font-semibold text-textoFraco"
              value={comprador}
              onChange={(e) => definirComprador(e.target.value)}
              aria-label="Comprador"
            >
              <option value="">Todos os compradores</option>
              {compradores.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            onClick={() => definirSoAntigas((v) => !v)}
            title="Só as notas emitidas há mais de 15 dias — para conferir e dispensar o que já foi tratado no Procfit"
            className={cn(
              'inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition',
              soAntigas ? 'border-status-atencao bg-status-atencaoFundo text-status-atencao' : 'border-borda bg-superficie text-textoFraco hover:border-bordaForte',
            )}
          >
            <Clock className="h-3.5 w-3.5" /> Emitidas há +15 dias
          </button>
          <select
            className="h-9 rounded-lg border border-borda bg-superficie px-2 text-xs font-semibold text-textoFraco"
            value={ordem}
            onChange={(e) => {
              definirOrdem(e.target.value as Ordem);
              definirOrdemCol(null);
            }}
            aria-label="Ordenar"
          >
            <option value="parada">Mais parada primeiro</option>
            <option value="recente">Mais recente na etapa</option>
            <option value="emissao">Emissão mais antiga</option>
            <option value="valor">Maior valor</option>
            <option value="fornecedor">Fornecedor (A–Z)</option>
          </select>
          <Botao variante="secundaria" tamanho="pequeno" disabled={!notas.length} onClick={exportar} title="Baixa a lista que está na tela (com os filtros) em planilha">
            <Download className="h-4 w-4" /> Excel
          </Botao>
        </div>

        {/* -------- de quem é a vez: barra empilhada + legenda clicável. Em
             grade, os cartões pareciam cabeçalho das colunas (Paulo, 28/10/2026). -------- */}
        <div className="border-b border-borda px-4 py-3">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-2xs font-bold uppercase tracking-wider text-textoSuave">De quem é a vez</p>
            <p className="text-xs text-textoSuave">
              <b className="tabular text-texto">{totalAberto}</b> nota(s) em aberto{time ? ' · clique de novo para tirar o filtro' : ' · clique num time para filtrar'}
            </p>
          </div>
          <div className="mt-2 flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-superficie2" aria-hidden>
            {porTime
              .filter((t) => t.total > 0)
              .map((t) => (
                <span
                  key={t.time}
                  className={cn('h-full transition-all duration-300 first:rounded-l-full last:rounded-r-full', t.faixa, time && time !== t.time && 'opacity-25')}
                  style={{ width: `${(t.total / Math.max(totalAberto, 1)) * 100}%` }}
                />
              ))}
          </div>
          <div className="mt-2.5 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {porTime.map((t) => {
              const ativo = time === t.time;
              const pct = totalAberto ? Math.round((t.total / totalAberto) * 100) : 0;
              const dica = [
                t.etapasDoTime.length ? t.etapasDoTime.map((x) => `${x.rotulo}: ${x.total}`).join('\n') : 'Sem pendência',
                t.maisAntigo ? `A mais antiga chegou à etapa ${haQuanto(t.maisAntigo)}` : null,
              ]
                .filter(Boolean)
                .join('\n');
              return (
                <button
                  key={t.time}
                  type="button"
                  onClick={() => definirTime(ativo ? null : t.time)}
                  title={dica}
                  aria-pressed={ativo}
                  className={cn(
                    'group rounded-lg px-2.5 py-1.5 text-left transition',
                    ativo ? cn(t.ativo, 'ring-1 ring-inset ring-borda') : 'hover:bg-superficie2',
                    !t.total && !ativo && 'opacity-45',
                  )}
                >
                  <span className="flex items-center gap-1.5">
                    <span aria-hidden className={cn('h-2 w-2 rounded-full', t.faixa)} />
                    <span className={cn('text-xs font-semibold', t.texto)}>{t.rotulo}</span>
                    {t.total ? <span className="text-2xs text-textoSuave">{pct}%</span> : null}
                  </span>
                  <span className="mt-0.5 flex items-baseline gap-2">
                    <span className="text-xl font-bold leading-none tabular text-texto">{t.total}</span>
                    <span className="truncate text-2xs text-textoSuave">
                      {t.etapasDoTime.length === 1 ? t.etapasDoTime[0]!.rotulo.toLowerCase() : t.total ? `${t.etapasDoTime.length} etapas` : 'sem pendência'}
                    </span>
                  </span>
                  {t.parados || t.velhas ? (
                    <span className="mt-1 flex flex-wrap gap-1">
                      {t.parados ? (
                        <span className="rounded-full bg-status-divergenciaFundo px-1.5 py-px text-2xs font-semibold text-status-divergencia">
                          {t.parados} há +24 h na etapa
                        </span>
                      ) : null}
                      {t.velhas ? (
                        <span className="rounded-full bg-status-atencaoFundo px-1.5 py-px text-2xs font-semibold text-status-atencao">
                          {t.velhas} emitida(s) há +7 dias
                        </span>
                      ) : null}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        {/* -------- a esteira: quatro fases em sequência, cada etapa numa linha -------- */}
        <div className="grid gap-3 bg-superficie2/50 p-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {FASES.map((fase, iFase) => {
            const totalFase = fase.status.reduce((s2, st) => s2 + (contagem.get(st)?.total ?? 0), 0);
            const concluidas = iFase === FASES.length - 1;
            const IconeFase = ICONE_FASE[iFase] ?? CheckCircle2;
            return (
              <div key={fase.titulo} className="relative flex flex-col rounded-xl border border-borda bg-superficie shadow-xs">
                {/* seta para a próxima fase (só em linha) */}
                {iFase < FASES.length - 1 ? (
                  <ChevronRight
                    aria-hidden
                    className="pointer-events-none absolute -right-[22px] top-3.5 z-10 hidden h-5 w-5 rounded-full border border-borda bg-superficie p-0.5 text-textoSuave shadow-xs lg:block"
                  />
                ) : null}
                <div className="flex items-center gap-2.5 border-b border-borda px-3 py-2.5">
                  {/* Um ícone por fase. Roxo cheio: tem nota; roxo claro: vazia;
                      verde: o que já terminou (Concluídas). */}
                  <span
                    className={cn(
                      'grid h-7 w-7 shrink-0 place-items-center rounded-full',
                      concluidas ? 'bg-status-livreFundo text-status-livre' : totalFase ? 'bg-primaria text-white' : 'bg-primaria-suave text-primaria',
                    )}
                    title={concluidas ? 'Já terminou' : totalFase ? 'Tem nota nesta fase' : 'Nenhuma nota nesta fase'}
                  >
                    <IconeFase className="h-3.5 w-3.5" />
                  </span>
                  <p className="min-w-0 flex-1 truncate text-[13px] font-semibold text-texto">{fase.titulo}</p>
                  <span className={cn('text-lg font-bold leading-none tabular', totalFase ? 'text-texto' : 'text-textoSuave')}>{totalFase}</span>
                </div>
                <ul className="flex-1 space-y-0.5 p-1.5">
                  {fase.status.map((st) => {
                    const e = etapas.get(st);
                    const c = contagem.get(st);
                    if (!e) return null;
                    const total = c?.total ?? 0;
                    const ativo = filtro === st;
                    // De quem é a vez nesta etapa ("Validada" fica na coluna da
                    // conferência, mas é o fiscal que dá a entrada).
                    const dono = TIMES.find((x) => x.time === e.dono);
                    const deOutroTime = Boolean(time) && e.dono !== time;
                    return (
                      <li key={st} className={cn('transition-opacity', deOutroTime && 'opacity-30')}>
                        <button
                          type="button"
                          title={e.explica}
                          onClick={() => definirFiltro(ativo ? 'todas' : st)}
                          aria-pressed={ativo}
                          className={cn(
                            'relative flex w-full items-center gap-2 rounded-lg py-1.5 pl-3 pr-2 text-left text-[13px] transition',
                            ativo ? 'bg-primaria-suave font-semibold text-texto' : total ? 'hover:bg-superficie2' : 'text-textoSuave hover:bg-superficie2',
                          )}
                        >
                          {ativo ? <span aria-hidden className="absolute inset-y-1 left-0 w-1 rounded-full bg-primaria" /> : null}
                          <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', PONTO[e.tom], !total && 'opacity-40')} />
                          <span className={cn('min-w-0 flex-1 truncate', total && !ativo && 'font-medium text-texto')}>{e.rotulo}</span>
                          {dono && !e.final && total ? (
                            <span
                              className={cn('inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-px text-2xs font-semibold', dono.ativo, dono.texto)}
                              title={`Vez de ${dono.rotulo}`}
                            >
                              <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', dono.faixa)} />
                              {dono.rotulo}
                            </span>
                          ) : null}
                          {c?.parados ? (
                            <span
                              className="shrink-0 rounded-full bg-status-divergenciaFundo px-1.5 py-px text-2xs font-bold text-status-divergencia"
                              title={`${c.parados} parada(s) há mais de 24 h`}
                            >
                              {c.parados} +24h
                            </span>
                          ) : null}
                          <span
                            className={cn(
                              'min-w-[2rem] shrink-0 rounded-md px-1.5 py-0.5 text-right text-xs font-bold tabular',
                              total ? 'bg-superficie2 text-texto' : 'opacity-50',
                              ativo && 'bg-superficie',
                            )}
                          >
                            {total}
                          </span>
                        </button>
                        {/* Em conferência aberta por fase da cega: o que falta guardar (Paulo, 29/10/2026).
                            "Alocado parcialmente" fica sempre à vista; as outras só com nota. */}
                        {st === 'em_conferencia' ? (
                          <ul className="ml-4 mt-0.5 space-y-0.5 border-l border-borda pl-1.5">
                            {SUBETAPAS_RECEBIMENTO.filter((s) => s.fase === 'parcial' || (consulta.data?.subetapas?.[s.fase]?.cegas ?? 0) > 0).map((s) => {
                              const sub = consulta.data?.subetapas?.[s.fase];
                              const n = sub?.cegas ?? 0;
                              const chaveFiltro = `fase:${s.fase}` as const;
                              const subAtivo = filtro === chaveFiltro;
                              return (
                                <li key={s.fase}>
                                  <button
                                    type="button"
                                    title={`${s.explica}${sub ? `\n${sub.cegas} cega(s), ${sub.notas} nota(s)` : ''}`}
                                    onClick={() => definirFiltro(subAtivo ? 'todas' : chaveFiltro)}
                                    aria-pressed={subAtivo}
                                    className={cn(
                                      'flex w-full items-center gap-2 rounded-md py-1 pl-2 pr-2 text-left text-xs transition',
                                      subAtivo ? 'bg-primaria-suave font-semibold text-texto' : n ? 'text-texto hover:bg-superficie2' : 'text-textoSuave hover:bg-superficie2',
                                    )}
                                  >
                                    <span className="min-w-0 flex-1 truncate">{s.rotulo}</span>
                                    {sub?.paradas && s.fase !== 'alocada' ? (
                                      <span
                                        className="shrink-0 rounded-full bg-status-divergenciaFundo px-1.5 py-px text-2xs font-bold text-status-divergencia"
                                        title={`${sub.paradas} cega(s) com nota há mais de 24 h em conferência`}
                                      >
                                        {sub.paradas} +24h
                                      </span>
                                    ) : null}
                                    <span
                                      className={cn(
                                        'min-w-[1.75rem] shrink-0 rounded-md px-1.5 py-px text-right font-bold tabular',
                                        n && s.fase === 'parcial' ? 'bg-status-atencaoFundo text-status-atencao' : n ? 'bg-superficie2' : 'opacity-50',
                                      )}
                                    >
                                      {n}
                                    </span>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        {/* -------- o que está filtrado -------- */}
        <div className="flex flex-wrap items-center gap-2 border-t border-borda bg-superficie2 px-3 py-2 text-xs text-textoFraco">
          <span>
            {consulta.isLoading ? 'Carregando…' : <><b className="text-texto">{notas.length}</b> nota(s)</>}
            {filtro === 'meu' && !statusDaConsulta ? ' esperando por você' : ''}
          </span>
          {filtrosAtivos.map((f) => (
            <span key={f.rotulo} className="inline-flex items-center gap-1 rounded-full bg-superficie px-2 py-0.5 font-semibold text-texto ring-1 ring-borda">
              {f.rotulo}
              <button type="button" aria-label={`Tirar filtro ${f.rotulo}`} className="text-textoSuave hover:text-texto" onClick={f.tirar}>
                ×
              </button>
            </span>
          ))}
          {filtrosAtivos.length > 1 ? (
            <button
              type="button"
              className={acaoTexto}
              onClick={() => {
                definirFiltro('todas');
                definirBusca('');
                definirSoAntigas(false);
              }}
            >
              Limpar tudo
            </button>
          ) : null}
          {statusDaConsulta && etapas.get(statusDaConsulta) ? (
            <span className="ml-auto hidden truncate md:inline">{subetapaFiltro?.explica ?? etapas.get(statusDaConsulta)!.explica}</span>
          ) : null}
        </div>
      </Cartao>

      {/* -------- a lista -------- */}
      <Cartao className="overflow-hidden">
        {consulta.isLoading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2, 3].map((i) => (
              <Esqueleto key={i} className="h-12" />
            ))}
          </div>
        ) : notas.length === 0 ? (
          <EstadoVazio
            titulo={filtrosAtivos.length ? 'Nenhuma nota com esses filtros' : filtro === 'meu' ? 'Nada esperando por você' : 'Nenhuma nota aqui'}
            detalhe={filtro === 'meu' && !filtrosAtivos.length ? 'Quando uma nota precisar de você, ela aparece aqui.' : undefined}
          />
        ) : (
          <>
          <div className="hidden grid-cols-12 gap-3 border-b border-borda bg-superficie2 py-2 pl-12 pr-[84px] text-2xs font-semibold uppercase tracking-wide text-textoSuave sm:grid">
            {colunas.map((c) => {
              const ativa = ordemCol?.chave === c.chave;
              const filtrada = Boolean(filtrosCol[c.chave]);
              return (
                <span key={c.chave} className={cn('group flex min-w-0 items-center gap-0.5', c.span.replace('sm:', ''), c.direita && 'justify-end')}>
                  <button
                    type="button"
                    title="Ordenar"
                    onClick={() => definirOrdemCol((o) => (o?.chave === c.chave ? { chave: c.chave, desc: !o.desc } : { chave: c.chave, desc: false }))}
                    className={cn('flex min-w-0 items-center gap-1 uppercase tracking-wide transition hover:text-primaria', ativa && 'text-primaria')}
                  >
                    <span className="truncate">{c.cabecalho}</span>
                    {ativa ? (
                      ordemCol!.desc ? <ArrowDown className="h-3 w-3 shrink-0" /> : <ArrowUp className="h-3 w-3 shrink-0" />
                    ) : (
                      <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-30" />
                    )}
                  </button>
                  <button
                    type="button"
                    aria-label={`Filtrar ${c.rotuloFiltro ?? c.cabecalho}`}
                    title={`Filtrar ${c.rotuloFiltro ?? c.cabecalho}`}
                    onClick={(e) => definirFiltroAberto({ chave: c.chave, ancora: e.currentTarget.getBoundingClientRect() })}
                    className={cn(
                      'grid h-5 w-5 shrink-0 place-items-center rounded transition',
                      filtrada ? 'bg-primaria text-white' : 'text-textoSuave opacity-50 hover:bg-superficie hover:text-texto group-hover:opacity-100',
                    )}
                  >
                    <Filter className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
          </div>
          {filtroAberto
            ? (() => {
                const c = colunas.find((x) => x.chave === filtroAberto.chave);
                if (!c) return null;
                return (
                  <FiltroDeColuna
                    titulo={c.rotuloFiltro ?? c.cabecalho}
                    ancora={filtroAberto.ancora}
                    linhas={notasBase.filter((n) => passaFiltros(n, c.chave))}
                    extrair={c.filtrar}
                    marcados={filtrosCol[c.chave] ?? null}
                    aoAplicar={(v) => definirFiltrosCol((f) => ({ ...f, [c.chave]: v }))}
                    aoOrdenar={(desc) => definirOrdemCol({ chave: c.chave, desc })}
                    aoFechar={() => definirFiltroAberto(null)}
                  />
                );
              })()
            : null}
          <div className="flex items-center justify-end gap-2 border-b border-borda px-4 py-1.5 text-xs text-textoFraco">
            <span>Ver</span>
            <div className="inline-flex overflow-hidden rounded-md border border-borda">
              {(['oc', 'fornecedor', 'lista'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={agrupar === v}
                  onClick={() => trocarAgrupar(v)}
                  className={cn('px-2.5 py-1 font-semibold transition', agrupar === v ? 'bg-primaria text-white' : 'bg-superficie hover:bg-superficie2')}
                >
                  {v === 'oc' ? 'Por OC' : v === 'fornecedor' ? 'Por fornecedor' : 'Lista'}
                </button>
              ))}
            </div>
          </div>
          {agrupar === 'fornecedor' ? (
            <NotasPorFornecedor
              notas={notas}
              ocs={consulta.data?.ocs ?? {}}
              slack={Boolean(consulta.data?.slack)}
              linha={(n) => (
                <LinhaNota
                  key={n.chave}
                  nota={n}
                  etapa={etapas.get(n.status)}
                  marcavel={marcavel(n)}
                  marcada={marcadas.has(n.chave)}
                  aoMarcar={() => alternar(n.chave)}
                  aoAbrir={() => aoAbrir(n.chave)}
                />
              )}
            />
          ) : agrupar === 'oc' ? (
            <NotasPorOc
              notas={notas}
              ocs={consulta.data?.ocs ?? {}}
              slack={Boolean(consulta.data?.slack)}
              linha={(n) => (
                <LinhaNota
                  key={n.chave}
                  nota={n}
                  etapa={etapas.get(n.status)}
                  marcavel={marcavel(n)}
                  marcada={marcadas.has(n.chave)}
                  aoMarcar={() => alternar(n.chave)}
                  aoAbrir={() => aoAbrir(n.chave)}
                />
              )}
            />
          ) : (
            <ul className="divide-y divide-borda">
              {notas.map((n) => (
                <LinhaNota
                  key={n.chave}
                  nota={n}
                  etapa={etapas.get(n.status)}
                  marcavel={marcavel(n)}
                  marcada={marcadas.has(n.chave)}
                  aoMarcar={() => alternar(n.chave)}
                  aoAbrir={() => aoAbrir(n.chave)}
                />
              ))}
            </ul>
          )}
          </>
        )}
      </Cartao>

      {/* Agendar várias de uma vez (uma carga traz mais de uma nota) ou dispensar várias (as antigas). */}
      {marcadas.size > 0 ? (
        <div className="sticky bottom-4 z-10 space-y-3 rounded-xl border border-borda bg-superficie px-4 py-3 shadow-forte">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm">
              <b>{marcadas.size}</b> nota(s) marcada(s)
              {marcaveis.length > marcadas.size ? (
                <button
                  type="button"
                  className={cn(acaoTexto, 'ml-2 text-xs')}
                  onClick={() => definirMarcadas(new Set(marcaveis.map((n) => n.chave)))}
                >
                  marcar todas as {marcaveis.length} da lista
                </button>
              ) : null}
            </span>
            <div className="flex gap-2">
              <Botao variante="fantasma" tamanho="pequeno" onClick={() => definirMarcadas(new Set())}>
                Limpar
              </Botao>
              {selecionadas.some(podeDispensarNota) && !dispensando ? (
                <Botao variante="secundaria" tamanho="pequeno" onClick={() => definirDispensando(true)}>
                  <Archive className="h-4 w-4" /> Dispensar
                </Botao>
              ) : null}
              {podeAgendar && todasAgendaveis ? (
                <Botao variante="primaria" tamanho="pequeno" onClick={() => aoAgendarLote([...marcadas])}>
                  <CalendarPlus className="h-4 w-4" /> Agendar juntas
                </Botao>
              ) : null}
            </div>
          </div>
          {dispensando ? (
            <EscolherMotivo
              quantas={marcadas.size}
              carregando={dispensar.isPending}
              erro={dispensar.error instanceof ErroHttp ? dispensar.error.message : dispensar.error ? String(dispensar.error) : null}
              aoConfirmar={(m) => dispensar.mutate(m)}
              aoCancelar={() => definirDispensando(false)}
            />
          ) : null}
          {dispensar.data?.recusadas.length ? (
            <p className="text-xs text-status-divergencia">
              {dispensar.data.feitas} dispensada(s). {dispensar.data.recusadas.length} não saiu(saíram) e continua(m) marcada(s):{' '}
              {dispensar.data.recusadas[0]!.motivo}
            </p>
          ) : null}
        </div>
      ) : marcaveis.length > 1 ? (
        <p className="px-4 text-xs text-textoFraco">
          Dica: marque notas para agendar a mesma carga junto, ou para dispensar de uma vez as que não precisam passar pela esteira.
        </p>
      ) : null}
    </div>
  );
}

/**
 * As notas da lista (já filtradas e ordenadas) agrupadas: um cartão por OC, na
 * ordem em que a primeira nota dela aparece; as sem OC no fim, um cartão por fornecedor.
 * Cada cartão recolhe e expande; o que está recolhido fica guardado no navegador.
 */
function NotasPorOc({
  notas,
  ocs,
  slack,
  linha,
}: {
  notas: Nota[];
  ocs: Record<number, ValorOc>;
  slack: boolean;
  linha: (n: Nota) => React.ReactNode;
}): JSX.Element {
  const porOc = new Map<number, Nota[]>();
  const semOc = new Map<string, Nota[]>();
  for (const n of notas) {
    if (n.pedidoCompra != null) porOc.set(n.pedidoCompra, [...(porOc.get(n.pedidoCompra) ?? []), n]);
    else {
      const k = nomeFornecedor(n.emitenteNome, n.emitenteUf) ?? 'FORNECEDOR SEM NOME';
      semOc.set(k, [...(semOc.get(k) ?? []), n]);
    }
  }
  const totalSemOc = [...semOc.values()].reduce((s, l) => s + l.length, 0);
  const acima = [...porOc.keys()].filter((oc) => ocs[oc]?.acima);
  const [soAcima, definirSoAcima] = React.useState(false);
  const { recolhidos, guardar, alternar } = useRecolhidos();
  const chaves = [...[...porOc.keys()].map((oc) => `oc:${oc}`), ...[...semOc.keys()].map((f) => `forn:${f}`)];
  const ocsVisiveis = [...porOc.entries()].filter(([oc]) => !soAcima || ocs[oc]?.acima);
  return (
    <div className="space-y-4 bg-superficie2/40 p-3">
      <BarraGrupos
        resumo={`${porOc.size} ${porOc.size === 1 ? 'OC' : 'OCs'}${totalSemOc ? ` · ${semOc.size} fornecedor(es) sem OC` : ''}`}
        acima={acima.length}
        soAcima={soAcima}
        aoSoAcima={() => definirSoAcima((v) => !v)}
        chaves={chaves}
        recolhidos={recolhidos}
        guardar={guardar}
      />
      {ocsVisiveis.map(([oc, l]) => (
        <CartaoGrupo
          key={oc}
          titulo={`OC ${oc}`}
          detalhe={[nomeFornecedor(l[0]!.emitenteNome, l[0]!.emitenteUf), l[0]!.compradorNome].filter(Boolean).join(' · ')}
          notas={l}
          linha={linha}
          oc={oc}
          valorOc={ocs[oc]}
          slack={slack}
          recolhido={recolhidos.has(`oc:${oc}`)}
          aoAlternar={() => alternar(`oc:${oc}`)}
        />
      ))}
      {totalSemOc && !soAcima ? (
        <section className="space-y-2">
          <p className="px-1 text-xs font-semibold text-textoFraco">
            Sem OC <span className="font-normal text-textoSuave">· {totalSemOc} nota(s) de {semOc.size} fornecedor(es)</span>
          </p>
          {[...semOc.entries()].map(([forn, l]) => (
            <CartaoGrupo
              key={forn}
              titulo={forn}
              notas={l}
              linha={linha}
              semOc
              recolhido={recolhidos.has(`forn:${forn}`)}
              aoAlternar={() => alternar(`forn:${forn}`)}
            />
          ))}
        </section>
      ) : null}
    </div>
  );
}

type Agrupar = 'oc' | 'fornecedor' | 'lista';

/** O que está recolhido, guardado no navegador (vale para os dois agrupamentos). */
function useRecolhidos(): { recolhidos: Set<string>; guardar: (novo: Set<string>) => void; alternar: (k: string) => void } {
  const [recolhidos, definirRecolhidos] = React.useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('wms.entradas.recolhidos') ?? '[]') as string[]);
    } catch {
      return new Set();
    }
  });
  const guardar = (novo: Set<string>): void => {
    definirRecolhidos(novo);
    try {
      localStorage.setItem('wms.entradas.recolhidos', JSON.stringify([...novo].slice(-300)));
    } catch {
      /* sem armazenamento: só não lembra */
    }
  };
  const alternar = (k: string): void => {
    const novo = new Set(recolhidos);
    if (novo.has(k)) novo.delete(k);
    else novo.add(k);
    guardar(novo);
  };
  return { recolhidos, guardar, alternar };
}

/** Resumo do agrupamento, "Só NFs acima da OC" e Recolher/Expandir todos. */
function BarraGrupos({
  resumo,
  acima,
  soAcima,
  aoSoAcima,
  chaves,
  recolhidos,
  guardar,
}: {
  resumo: string;
  acima: number;
  soAcima: boolean;
  aoSoAcima: () => void;
  chaves: string[];
  recolhidos: Set<string>;
  guardar: (novo: Set<string>) => void;
}): JSX.Element {
  const todosRecolhidos = chaves.length > 0 && chaves.every((k) => recolhidos.has(k));
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-textoFraco">
      <span>{resumo}</span>
      <span className="flex items-center gap-2">
        {acima ? (
          <button
            type="button"
            aria-pressed={soAcima}
            onClick={aoSoAcima}
            className={cn(
              'rounded-full border px-2.5 py-1 font-semibold transition',
              soAcima
                ? 'border-status-divergencia bg-status-divergencia text-white'
                : 'border-status-divergencia/40 bg-status-divergenciaFundo text-status-divergencia hover:border-status-divergencia',
            )}
          >
            {soAcima ? 'Mostrando só' : 'Só'} NFs acima da OC ({acima})
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => guardar(todosRecolhidos ? new Set([...recolhidos].filter((k) => !chaves.includes(k))) : new Set([...recolhidos, ...chaves]))}
          className="inline-flex items-center gap-1 rounded-md border border-borda bg-superficie px-2.5 py-1 font-semibold hover:bg-superficie2"
        >
          {todosRecolhidos ? <ChevronsUpDown className="h-3.5 w-3.5" /> : <ChevronsDownUp className="h-3.5 w-3.5" />}
          {todosRecolhidos ? 'Expandir todos' : 'Recolher todos'}
        </button>
      </span>
    </div>
  );
}

/**
 * Três níveis: fornecedor (pelo CNPJ) → as OCs dele → as notas de cada OC. As
 * notas do fornecedor sem OC ficam numa caixa "Sem OC" dentro dele. Mesma ordem
 * da lista: o fornecedor, e a OC dentro dele, entram onde aparece a primeira nota.
 */
function NotasPorFornecedor({
  notas,
  ocs,
  slack,
  linha,
}: {
  notas: Nota[];
  ocs: Record<number, ValorOc>;
  slack: boolean;
  linha: (n: Nota) => React.ReactNode;
}): JSX.Element {
  type Grupo = { nome: string; cnpj: string; porOc: Map<number, Nota[]>; semOc: Nota[]; todas: Nota[] };
  const grupos = new Map<string, Grupo>();
  for (const n of notas) {
    const k = n.emitenteCnpj || n.emitenteNome || '?';
    const g: Grupo = grupos.get(k) ?? { nome: nomeFornecedor(n.emitenteNome, n.emitenteUf) ?? 'FORNECEDOR SEM NOME', cnpj: n.emitenteCnpj, porOc: new Map(), semOc: [], todas: [] };
    g.todas.push(n);
    if (n.pedidoCompra != null) g.porOc.set(n.pedidoCompra, [...(g.porOc.get(n.pedidoCompra) ?? []), n]);
    else g.semOc.push(n);
    grupos.set(k, g);
  }
  const [soAcima, definirSoAcima] = React.useState(false);
  const { recolhidos, guardar, alternar } = useRecolhidos();
  const todasOcs = [...grupos.values()].flatMap((g) => [...g.porOc.keys()]);
  const acima = todasOcs.filter((oc) => ocs[oc]?.acima);
  const chaves = [...grupos.keys()].map((k) => `fornoc:${k}`);
  const visiveis = [...grupos.entries()].filter(([, g]) => !soAcima || [...g.porOc.keys()].some((oc) => ocs[oc]?.acima));
  return (
    <div className="space-y-4 bg-superficie2/40 p-3">
      <BarraGrupos
        resumo={`${grupos.size} ${grupos.size === 1 ? 'fornecedor' : 'fornecedores'} · ${todasOcs.length} ${todasOcs.length === 1 ? 'OC' : 'OCs'}`}
        acima={acima.length}
        soAcima={soAcima}
        aoSoAcima={() => definirSoAcima((v) => !v)}
        chaves={chaves}
        recolhidos={recolhidos}
        guardar={guardar}
      />
      {visiveis.map(([k, g]) => {
        const recolhido = recolhidos.has(`fornoc:${k}`);
        const valor = g.todas.reduce((s, n) => s + (n.valor ?? 0), 0);
        const divergentes = g.todas.filter((n) => n.divergenciasAbertas > 0).length;
        const ocsAcima = [...g.porOc.keys()].filter((oc) => ocs[oc]?.acima).length;
        const ocsDoGrupo = [...g.porOc.entries()].filter(([oc]) => !soAcima || ocs[oc]?.acima);
        return (
          <div
            key={k}
            className={cn('min-w-0 overflow-hidden rounded-xl border bg-superficie shadow-sm', ocsAcima ? 'border-status-divergencia/50' : 'border-bordaForte')}
          >
            <div
              role="button"
              tabIndex={0}
              aria-expanded={!recolhido}
              onClick={() => alternar(`fornoc:${k}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  alternar(`fornoc:${k}`);
                }
              }}
              className={cn(
                'flex cursor-pointer select-none flex-wrap items-center justify-between gap-x-4 gap-y-1.5 bg-superficie2 px-4 py-2.5',
                !recolhido && 'border-b border-borda',
              )}
            >
              <p className="flex min-w-0 items-center gap-2">
                {recolhido ? <ChevronRight className="h-4 w-4 shrink-0 text-textoFraco" /> : <ChevronDown className="h-4 w-4 shrink-0 text-textoFraco" />}
                <span className="shrink-0 rounded-md bg-texto px-2 py-0.5 text-2xs font-bold uppercase tracking-wide text-superficie">Fornecedor</span>
                <b className="truncate text-sm text-texto">{g.nome}</b>
                {g.cnpj ? <span className="shrink-0 text-xs tabular-nums text-textoFraco">{cnpjFormatado(g.cnpj)}</span> : null}
              </p>
              <div className="flex shrink-0 flex-wrap items-center gap-2 text-xs">
                {ocsAcima ? (
                  <span className="rounded-full bg-status-divergencia px-2 py-0.5 font-bold text-white">
                    ▲ {ocsAcima} {ocsAcima === 1 ? 'OC' : 'OCs'} com NFs acima
                  </span>
                ) : null}
                <span className="rounded-full border border-borda bg-superficie px-2 py-0.5 text-textoFraco">
                  <b className="text-texto">{g.porOc.size}</b> {g.porOc.size === 1 ? 'OC' : 'OCs'}
                  {g.semOc.length ? ` + ${g.semOc.length} sem OC` : ''}
                </span>
                <span className="rounded-full border border-borda bg-superficie px-2 py-0.5 text-textoFraco">
                  <b className="text-texto">{g.todas.length}</b> {g.todas.length === 1 ? 'nota' : 'notas'}
                </span>
                <span className="rounded-full border border-borda bg-superficie px-2 py-0.5 font-semibold tabular-nums text-texto">{moeda(valor)}</span>
                {divergentes ? (
                  <span className="rounded-full bg-status-divergenciaFundo px-2 py-0.5 font-semibold text-status-divergencia">{divergentes} com divergência</span>
                ) : null}
              </div>
            </div>
            {recolhido ? null : (
              // As OCs recuadas dentro do fornecedor: o mesmo cartão do "Por OC", sem repetir o fornecedor.
              <div className="space-y-2 bg-superficie2/40 py-2.5 pl-6 pr-2.5">
                {ocsDoGrupo.map(([oc, l]) => (
                  <CartaoGrupo
                    key={oc}
                    titulo={`OC ${oc}`}
                    detalhe={l[0]!.compradorNome ?? undefined}
                    notas={l}
                    linha={linha}
                    oc={oc}
                    valorOc={ocs[oc]}
                    slack={slack}
                    aninhado
                    recolhido={recolhidos.has(`oc:${oc}`)}
                    aoAlternar={() => alternar(`oc:${oc}`)}
                  />
                ))}
                {g.semOc.length && !soAcima ? (
                  <CartaoGrupo
                    titulo="aguardando a OC"
                    notas={g.semOc}
                    linha={linha}
                    semOc
                    aninhado
                    recolhido={recolhidos.has(`forn:${g.nome}`)}
                    aoAlternar={() => alternar(`forn:${g.nome}`)}
                  />
                ) : null}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const cnpjFormatado = (c: string): string =>
  /^\d{14}$/.test(c) ? `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}` : c;

const pct = (v: number): string => `${v.toLocaleString('pt-BR', { maximumFractionDigits: v >= 100 ? 0 : 1 })}%`;

function CartaoGrupo({
  titulo,
  detalhe,
  notas,
  linha,
  semOc,
  oc,
  valorOc,
  slack,
  aninhado,
  recolhido,
  aoAlternar,
}: {
  titulo: string;
  detalhe?: string | undefined;
  notas: Nota[];
  linha: (n: Nota) => React.ReactNode;
  semOc?: boolean;
  oc?: number;
  valorOc?: ValorOc | undefined;
  slack?: boolean;
  /** Dentro do cartão do fornecedor (Por fornecedor): mais leve, e "Sem OC" no selo. */
  aninhado?: boolean;
  recolhido: boolean;
  aoAlternar: () => void;
}): JSX.Element {
  const cliente = useQueryClient();
  const valor = notas.reduce((s, n) => s + (n.valor ?? 0), 0);
  const divergentes = notas.filter((n) => n.divergenciasAbertas > 0).length;
  // O tipo junto da contagem: "2 com divergência" sozinho não diz o que olhar.
  const tiposDiv = [...new Set(notas.flatMap((n) => (n.divergenciasResumo ?? []).map((x) => TIPO_DIVERGENCIA[x.regra] ?? x.regra)))];
  const acima = Boolean(valorOc?.acima);
  const avisar = useMutation({
    mutationFn: () => api.post<{ ok: true }>(`/api/entradas/oc/${oc}/avisar-valor`, {}),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: ['entradas'] }),
  });
  const pre = valorOc?.prePedido ?? null;
  const [verPre, definirVerPre] = React.useState(false);
  const resumoPre = pre
    ? (['fora', 'removido', 'quantidade', 'valor'] as const)
        .map((t) => [t, pre.itens.filter((i) => i.tipo === t).length] as const)
        .filter(([, n]) => n > 0)
        .map(([t, n]) => `${n} ${ROTULO_OC_PRE[t].toLowerCase()}`)
        .join(', ')
    : '';
  const difPre = pre ? pre.itens.reduce((s, i) => s + difItemPre(i), 0) : 0;
  const dif = valorOc ? valorOc.valorNotas - valorOc.valorOc : 0;
  // Quanto da OC já veio em nota: a OC "enchendo" aparece antes de passar.
  const usado = valorOc && valorOc.valorOc > 0 ? (valorOc.valorNotas / valorOc.valorOc) * 100 : null;
  // Abaixo de 99,5% da OC = o fornecedor ainda não faturou tudo (arredondamento não conta).
  const parcial = usado != null && usado < 99.5;
  const explicacao = valorOc
    ? `Valor da OC: ${moeda(valorOc.valorOc)}\nSoma das NFs da OC: ${moeda(valorOc.valorNotas)} (${valorOc.notas} ${valorOc.notas === 1 ? 'nota' : 'notas'} na esteira, não só as deste filtro)` +
      (acima ? `\nDiferença: +${moeda(dif)} (+${pct((dif / valorOc.valorOc) * 100)})` : '')
    : undefined;
  return (
    // O cabeçalho de cada cartão é o que separa uma OC da outra; vermelho claro quando as NFs passam da OC.
    <div
      className={cn(
        'min-w-0 overflow-hidden border bg-superficie',
        aninhado ? 'rounded-lg' : 'rounded-xl shadow-sm',
        acima ? 'border-status-divergencia/50' : aninhado ? 'border-borda' : 'border-bordaForte',
      )}
    >
      <div
        role="button"
        tabIndex={0}
        aria-expanded={!recolhido}
        onClick={aoAlternar}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            aoAlternar();
          }
        }}
        className={cn(
          'flex cursor-pointer select-none flex-wrap items-center justify-between gap-x-4 gap-y-1.5 px-4 py-2.5',
          !recolhido && 'border-b',
          acima ? 'border-status-divergencia/30 bg-status-divergenciaFundo' : 'border-primaria/30 bg-primaria-suave2',
        )}
      >
        <p className="flex min-w-0 items-center gap-2">
          {recolhido ? <ChevronRight className="h-4 w-4 shrink-0 text-textoFraco" /> : <ChevronDown className="h-4 w-4 shrink-0 text-textoFraco" />}
          <span
            className={cn(
              'shrink-0 rounded-md px-2 py-0.5 text-2xs font-bold uppercase tracking-wide',
              semOc ? 'bg-status-atencaoFundo text-status-atencao' : acima ? 'bg-status-divergencia text-white' : 'bg-primaria text-white',
            )}
          >
            {semOc ? (aninhado ? 'Sem OC' : 'Fornecedor') : 'OC'}
          </span>
          <b className="truncate text-sm text-texto">{semOc ? titulo : titulo.replace(/^OC\s*/, '')}</b>
          {/* O pré-pedido de onde a OC saiu, discreto ao lado do número. */}
          {pre ? (
            <span className="shrink-0 text-xs text-textoFraco" title={`Pré-pedido ${pre.numero} (o aprovado)`}>
              · pré-pedido <span className="tabular-nums">{pre.numero}</span>
            </span>
          ) : null}
          {detalhe ? <span className="truncate text-[13px] text-textoFraco">{detalhe}</span> : null}
        </p>
        <div className="flex shrink-0 flex-wrap items-center gap-2 text-xs">
          {acima && valorOc ? (
            <span title={explicacao} className="inline-flex items-center gap-1 rounded-full bg-status-divergencia px-2 py-0.5 font-bold text-white">
              ▲ NFs acima da OC · +{moeda(dif)} (+{pct((dif / valorOc.valorOc) * 100)})
            </span>
          ) : null}
          <span className="rounded-full border border-borda bg-superficie px-2 py-0.5 text-textoFraco">
            <b className="text-texto">{notas.length}</b> {notas.length === 1 ? 'nota' : 'notas'}
          </span>
          <span className="rounded-full border border-borda bg-superficie px-2 py-0.5 font-semibold tabular-nums text-texto">{moeda(valor)}</span>
          {valorOc && valorOc.valorOc > 0 ? (
            acima ? (
              <span title={explicacao} className="rounded-full border border-status-divergencia/50 bg-superficie px-2 py-0.5 font-semibold tabular-nums text-status-divergencia">
                NFs {moeda(valorOc.valorNotas)} / OC {moeda(valorOc.valorOc)}
              </span>
            ) : (
              // Quanto da OC o fornecedor já faturou: parcial (com a barrinha) ou total.
              <span
                title={`${explicacao ?? ''}\n${parcial ? `Falta faturar ${moeda(valorOc.valorOc - valorOc.valorNotas)}.` : 'O fornecedor já faturou a OC inteira.'}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-borda bg-superficie px-2 py-0.5 tabular-nums text-textoFraco"
              >
                {parcial ? (
                  <>
                    <span className="h-1.5 w-12 overflow-hidden rounded-full bg-superficie2" aria-hidden>
                      <span className="block h-full rounded-full bg-primaria/60" style={{ width: `${Math.min(100, usado ?? 0)}%` }} />
                    </span>
                    Faturamento parcial · {pct(usado ?? 0)} de {moeda(valorOc.valorOc)}
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-3 w-3 text-status-livre" /> Faturado total · {moeda(valorOc.valorOc)}
                  </>
                )}
              </span>
            )
          ) : null}
          {divergentes ? (
            <span className="rounded-full bg-status-divergenciaFundo px-2 py-0.5 font-semibold text-status-divergencia">
              {divergentes} com divergência{tiposDiv.length ? `: ${tiposDiv.slice(0, 2).join(', ').toLowerCase()}${tiposDiv.length > 2 ? '…' : ''}` : ''}
            </span>
          ) : null}
          {pre?.itens.length ? (
            <button
              type="button"
              aria-expanded={verPre}
              onClick={(e) => {
                e.stopPropagation();
                definirVerPre((v) => !v);
              }}
              onKeyDown={(e) => e.stopPropagation()}
              title={`A OC difere do pré-pedido ${pre.numero} aprovado: ${resumoPre}. Clique para ver os itens.`}
              className="inline-flex items-center gap-1 rounded-full border border-status-atencao/50 bg-status-atencaoFundo px-2 py-0.5 font-semibold text-status-atencao hover:border-status-atencao"
            >
              OC × pré-pedido · {pre.itens.length} {pre.itens.length === 1 ? 'item' : 'itens'} · {difTexto(difPre)}
              {verPre ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            </button>
          ) : null}
          {!semOc && valorOc && !pre ? (
            <span
              title="Esta OC não saiu de um pré-pedido aprovado: não há com o que comparar quantidade e preço. Vale confirmar com o comprador."
              className="rounded-full border border-dashed border-status-atencao/60 bg-superficie px-2 py-0.5 font-semibold text-status-atencao"
            >
              Sem pré-pedido
            </span>
          ) : null}
          {acima && slack && oc != null ? (
            <button
              type="button"
              disabled={avisar.isPending}
              onClick={(e) => {
                e.stopPropagation();
                avisar.mutate();
              }}
              onKeyDown={(e) => e.stopPropagation()}
              title={
                valorOc?.avisado
                  ? `Avisado ${haQuanto(valorOc.avisado.quando)}${valorOc.avisado.autor ? ` por ${valorOc.avisado.autor}` : ''}. Clique para avisar de novo.`
                  : 'Posta no canal de compras, marcando o comprador da OC, o valor da OC e a soma das NFs. Fica na linha do tempo das notas.'
              }
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-semibold transition disabled:opacity-60',
                valorOc?.avisado
                  ? 'border-borda bg-superficie text-textoFraco hover:bg-superficie2'
                  : 'border-status-divergencia bg-superficie text-status-divergencia hover:bg-status-divergencia hover:text-white',
              )}
            >
              <MessageSquare className="h-3.5 w-3.5" />
              {avisar.isPending ? 'Avisando…' : valorOc?.avisado ? `Comprador avisado ${haQuanto(valorOc.avisado.quando)}` : 'Avisar comprador no Slack'}
            </button>
          ) : null}
        </div>
        {avisar.error ? (
          <p className="basis-full text-right text-xs text-status-divergencia">
            {avisar.error instanceof ErroHttp ? avisar.error.message : String(avisar.error)}
          </p>
        ) : null}
      </div>
      {pre?.itens.length && verPre ? <DetalhePrePedido pre={pre} /> : null}
      {recolhido ? null : <ul className="divide-y divide-borda">{notas.map((n) => linha(n))}</ul>}
    </div>
  );
}

const ROTULO_OC_PRE: Record<TipoOcPre, string> = {
  fora: 'Fora do pré-pedido',
  removido: 'Saiu da OC',
  quantidade: 'Quantidade acima',
  valor: 'Valor acima',
};

/** Valor da OC − valor do pré-pedido: SKU fora do pré-pedido conta inteiro; o que saiu da OC, negativo. */
const difItemPre = (i: OcPrePedido['itens'][number]): number => (i.valorOc ?? 0) - (i.valorPre ?? 0);
const difTexto = (v: number): string => `${v > 0 ? '+' : v < 0 ? '−' : ''}${moeda(Math.abs(v))}`;

/** Os itens em que a OC difere do pré-pedido, numa caixa recuada com colunas próprias. */
function DetalhePrePedido({ pre }: { pre: OcPrePedido }): JSX.Element {
  const qtd = (v: number | null): string => (v == null ? '—' : v.toLocaleString('pt-BR', { maximumFractionDigits: 3 }));
  return (
    <div className="border-b border-borda bg-superficie2/50 px-4 py-2.5">
      <div className="ml-6 overflow-x-auto rounded-lg border border-status-atencao/30 bg-superficie">
        <table className="w-full min-w-[560px] text-xs">
          <thead className="bg-status-atencaoFundo/60 text-left text-textoFraco">
            <tr>
              <th className="px-3 py-1.5 font-semibold">SKU</th>
              <th className="px-3 py-1.5 font-semibold">O que difere</th>
              <th className="px-3 py-1.5 text-right font-semibold">Qtd OC</th>
              <th className="px-3 py-1.5 text-right font-semibold">Qtd pré {pre.numero}</th>
              <th className="px-3 py-1.5 text-right font-semibold">Valor OC</th>
              <th className="px-3 py-1.5 text-right font-semibold">Valor pré</th>
              <th className="px-3 py-1.5 text-right font-semibold">Diferença</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-borda">
            {pre.itens.map((i) => (
              <tr key={`${i.produto}-${i.tipo}`}>
                <td className="max-w-[260px] truncate px-3 py-1.5" title={i.descricao ?? undefined}>
                  <b className="tabular-nums">{i.produto}</b> <span className="text-textoFraco">{i.descricao}</span>
                </td>
                <td className="px-3 py-1.5 font-semibold text-status-atencao">{ROTULO_OC_PRE[i.tipo]}</td>
                <td className={cn('px-3 py-1.5 text-right tabular-nums', i.tipo === 'quantidade' && 'font-bold text-status-divergencia')}>{qtd(i.qtdOc)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-textoFraco">{qtd(i.qtdPre)}</td>
                <td className={cn('px-3 py-1.5 text-right tabular-nums', i.tipo === 'valor' && 'font-bold text-status-divergencia')}>
                  {i.valorOc == null ? '—' : moeda(i.valorOc)}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-textoFraco">{i.valorPre == null ? '—' : moeda(i.valorPre)}</td>
                <td className={cn('px-3 py-1.5 text-right font-bold tabular-nums', difItemPre(i) > 0 ? 'text-status-divergencia' : 'text-textoFraco')}>
                  {difTexto(difItemPre(i))}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t border-borda bg-superficie2/60">
            <tr>
              <td colSpan={6} className="px-3 py-1.5 text-right font-semibold text-textoFraco">
                Total OC − pré-pedido {pre.numero}
              </td>
              <td className="px-3 py-1.5 text-right font-bold tabular-nums text-status-divergencia">{difTexto(pre.itens.reduce((s, i) => s + difItemPre(i), 0))}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

type Ordem = 'parada' | 'recente' | 'emissao' | 'valor' | 'fornecedor';

interface ColunaEsteira {
  chave: string;
  cabecalho: string;
  /** Nome no filtro, quando o cabeçalho não diz o que se filtra. */
  rotuloFiltro?: string;
  span: string;
  direita?: boolean;
  filtrar: (n: Nota) => string;
  comparar: (a: Nota, b: Nota) => number;
}

/** Conferência com a OC, Agendamento, Recebimento, Concluídas — na ordem de FASES. */
const ICONE_FASE = [FileSearch, CalendarClock, PackageOpen, CheckCircle2];

const PONTO: Record<Etapa['tom'], string> = {
  neutro: 'bg-bordaForte',
  marca: 'bg-primaria',
  ok: 'bg-status-livre',
  alerta: 'bg-status-atencao',
  erro: 'bg-status-divergencia',
};

function ordenar(notas: Nota[], ordem: Ordem): Nota[] {
  const t = (v: string | null): number => (v ? new Date(v).getTime() : 0);
  const copia = [...notas];
  switch (ordem) {
    case 'recente':
      return copia.sort((a, b) => t(b.statusDesde) - t(a.statusDesde));
    case 'emissao':
      return copia.sort((a, b) => t(a.emissao) - t(b.emissao));
    case 'valor':
      return copia.sort((a, b) => (b.valor ?? 0) - (a.valor ?? 0));
    case 'fornecedor':
      return copia.sort((a, b) => (nomeFornecedor(a.emitenteNome, a.emitenteUf) ?? '').localeCompare(nomeFornecedor(b.emitenteNome, b.emitenteUf) ?? '', 'pt-BR'));
    default:
      return copia.sort((a, b) => t(a.statusDesde) - t(b.statusDesde));
  }
}

function LinhaNota({
  nota,
  etapa,
  marcavel,
  marcada,
  aoMarcar,
  aoAbrir,
}: {
  nota: Nota;
  etapa: Etapa | undefined;
  marcavel: boolean;
  marcada: boolean;
  aoMarcar: () => void;
  aoAbrir: () => void;
}): JSX.Element {
  const contexto =
    nota.status === 'agendada' || nota.status === 'em_transito_full' || nota.status === 'aguardando_transferencia'
      ? `${nota.destinoNome ?? ''}${nota.agendaData ? ` · ${nota.agendaData.split('-').reverse().join('/')}` : ''}${nota.agendaJanela ? ` ${nota.agendaJanela}` : ''}`
      : nota.jaTransferida
        ? textoJaTransferida(nota.jaTransferida)
        : nota.compraSemValidacao
          ? `Compra ${nota.nfCompra} não validada no ERP: fiscal valida na gaveta (título e estoque)`
        : nota.status === 'aguardando_agendamento' && nota.preDestino
          ? nota.preDestino.texto
        : nota.status === 'em_conferencia' && nota.recebimentoProgresso
          ? textoProgresso(nota.recebimentoProgresso)
          : ['sem_oc', 'divergente', 'recusada', 'divergencia_recebimento', 'validada'].includes(nota.status)
          ? nota.motivo
          : null;
  // Idade da NF: o atraso real (a etapa só conta desde que o WMS a viu).
  const diasNf = nota.emissao ? Math.floor((Date.now() - new Date(nota.emissao).getTime()) / 86_400_000) : null;
  const dono = etapa && !etapa.final ? TIMES.find((x) => x.time === etapa.dono) : undefined;
  const conversa = nota.conversaUltima ?? null;
  const esperandoResposta = conversa?.sentido === 'RECEBIDA';
  return (
    <li className={cn('flex items-center gap-3 px-4 py-3 transition-colors hover:bg-superficie2', marcada && 'bg-primaria-suave/40')}>
      {marcavel ? (
        <button
          type="button"
          role="checkbox"
          aria-checked={marcada}
          onClick={aoMarcar}
          aria-label={`Marcar NF ${nota.numero}`}
          className={cn(
            'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border text-[11px] font-bold transition',
            marcada ? 'border-primaria bg-primaria text-white' : 'border-bordaForte bg-superficie hover:border-primaria',
          )}
        >
          {marcada ? '✓' : ''}
        </button>
      ) : (
        <span className="w-5 shrink-0" />
      )}
      <button type="button" onClick={aoAbrir} className="grid min-w-0 flex-1 grid-cols-12 items-center gap-3 text-left">
        <span className="col-span-12 min-w-0 sm:col-span-4">
          <span className="block truncate text-sm font-semibold">{nomeFornecedor(nota.emitenteNome, nota.emitenteUf) ?? nota.emitenteCnpj}</span>
          <span className="text-xs text-textoFraco">
            NF {nota.numero ?? '—'}/{nota.serie ?? ''} · {moeda(nota.valor)}
            {nota.pedidoCompra ? ` · OC ${nota.pedidoCompra}` : ''}
            {nota.compradorNome ? ` · ${nota.compradorNome}` : ''}
          </span>
        </span>
        <span className="col-span-6 sm:col-span-2">
          {etapa ? <Pilula tom={etapa.tom}>{etapa.rotulo}</Pilula> : null}
          {/* Seguiu na esteira (entrada feita no ERP) com divergência da OC sem decisão. */}
          {nota.divergenciasAbertas > 0 && !['divergente', 'sem_oc', 'recusada'].includes(nota.status) ? (
            <span className="mt-1 block">
              <Pilula tom="erro" title={`${nota.divergenciasAbertas} divergência(s) da OC em aberto`}>
                entrou com divergência
              </Pilula>
            </span>
          ) : null}
          {/* Entrou divergente e já foi aprovada: continua visível, mas fora da cobrança (03/11/2026). */}
          {nota.divergenciasAbertas === 0 && nota.aprovacao ? (
            <span className="mt-1 block">
              <Pilula
                tom="ok"
                title={`Aprovada${nota.aprovacao.por ? ` por ${nota.aprovacao.por}` : ''} em ${new Date(nota.aprovacao.quando).toLocaleString('pt-BR')}: ${nota.aprovacao.motivo}`}
              >
                divergência aprovada
              </Pilula>
            </span>
          ) : null}
          {/* A esteira andou (guardada), mas a chegada não bateu com a nota: fica o selo. */}
          {nota.chegouDivergente ? (
            <span className="mt-1 block">
              <Pilula tom="erro" title="Chegou com a contagem diferente da nota; a linha do tempo tem o detalhe">
                chegou divergente
              </Pilula>
            </span>
          ) : null}
        </span>
        {/* Parado com: o time da vez, coluna própria (pedido do Paulo, 28/10/2026). */}
        <span className="col-span-6 sm:col-span-2">
          {dono ? (
            <span
              className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold', dono.ativo, dono.texto)}
              title={`Vez de ${dono.rotulo}`}
            >
              <span aria-hidden className={cn('h-2 w-2 rounded-full', dono.faixa)} />
              {dono.rotulo}
            </span>
          ) : (
            <span className="text-xs text-textoSuave">—</span>
          )}
        </span>
        <span className="col-span-12 min-w-0 text-xs text-textoFraco sm:col-span-2">
          {nota.divergenciasAbertas > 0 && nota.divergenciasResumo?.length ? (
            <ResumoDivergencias nota={nota} />
          ) : (
            <span
              className={cn(
                'block truncate',
                ((nota.status === 'aguardando_agendamento' && nota.preDestino?.tipo === 'consumo') || nota.jaTransferida || nota.compraSemValidacao) && 'text-status-atencao',
              )}
              title={contexto ?? undefined}
            >
              {contexto ?? ''}
            </span>
          )}
        </span>
        <span className="col-span-12 flex flex-col items-end gap-1 text-right text-xs tabular text-textoFraco sm:col-span-2">
          <span className="flex items-center gap-1.5">
            {conversa ? (
              <span
                className={cn(
                  'inline-flex items-center gap-0.5 rounded-full px-1.5 py-px text-2xs font-semibold',
                  esperandoResposta ? 'bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300' : 'text-textoSuave',
                )}
                title={
                  esperandoResposta
                    ? `${conversa.autor ?? 'Alguém'} respondeu no Slack ${haQuanto(conversa.quando)} e ninguém falou depois`
                    : `Última mensagem no Slack ${haQuanto(conversa.quando)}${conversa.autor ? `, de ${conversa.autor}` : ''}`
                }
              >
                <MessageSquare className="h-3 w-3" />
                {esperandoResposta ? 'resposta' : null}
              </span>
            ) : null}
            {diasNf != null ? (
              <span
                className={cn(
                  'rounded-full px-1.5 py-px text-2xs font-semibold',
                  diasNf > 15
                    ? 'bg-status-divergenciaFundo text-status-divergencia'
                    : diasNf > 7
                      ? 'bg-status-atencaoFundo text-status-atencao'
                      : 'bg-superficie2 text-textoFraco',
                )}
                title={`Emitida em ${new Date(nota.emissao!).toLocaleDateString('pt-BR')}`}
              >
                NF de {diasNf === 0 ? 'hoje' : diasNf === 1 ? '1 dia' : `${diasNf} dias`}
              </span>
            ) : null}
          </span>
          <span title="Há quanto tempo está nesta etapa">na etapa {haQuanto(nota.statusDesde)}</span>
        </span>
      </button>
      <AtalhosNota nota={nota} />
    </li>
  );
}

/** Na ponta da linha, sem abrir a nota: ver a DANFE e copiar a chave da NF-e. */
function AtalhosNota({ nota }: { nota: Nota }): JSX.Element {
  const [copiada, definirCopiada] = React.useState(false);
  const icone = 'flex h-7 w-7 items-center justify-center rounded-md text-textoSuave transition hover:bg-primaria-suave hover:text-primaria';
  return (
    <span className="flex shrink-0 items-center gap-0.5">
      {nota.resumo ? (
        <span className={cn(icone, 'cursor-not-allowed opacity-40 hover:bg-transparent hover:text-textoSuave')} title="DANFE disponível quando o portal fiscal baixar o XML completo">
          <FileText className="h-4 w-4" />
        </span>
      ) : (
        <a className={icone} href={`/api/entradas/nota/${nota.chave}/arquivo/pdf`} target="_blank" rel="noopener noreferrer" title="Ver DANFE" aria-label={`Ver DANFE da NF ${nota.numero ?? ''}`}>
          <FileText className="h-4 w-4" />
        </a>
      )}
      <button
        type="button"
        onClick={() =>
          void navigator.clipboard?.writeText(nota.chave).then(() => {
            definirCopiada(true);
            window.setTimeout(() => definirCopiada(false), 1500);
          })
        }
        className={cn(icone, copiada && 'bg-status-livreFundo text-status-livre hover:bg-status-livreFundo hover:text-status-livre')}
        title={copiada ? 'Chave copiada' : `Copiar a chave da NF-e\n${nota.chave.replace(/(\d{4})(?=\d)/g, '$1 ')}`}
        aria-label="Copiar a chave da NF-e"
      >
        {copiada ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </span>
  );
}

/**
 * O que diverge, na própria linha: tipo, SKU e nota × OC. A primeira
 * divergência por extenso; as outras contadas.
 */
function ResumoDivergencias({ nota }: { nota: Nota }): JSX.Element {
  const lista = nota.divergenciasResumo ?? [];
  const [p] = lista;
  const brl = (v: number): string => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const detalhe = (): string | null => {
    if (!p) return null;
    // Preço: Nota × Pré-pedido (o aprovado), e se a OC foi mexida depois.
    const pr = p.regra === 'preco' && p.mensagem ? precosDaMensagem(p.mensagem) : null;
    if (pr?.pre != null) {
      const mexida = pr.oc != null && Math.abs(pr.oc - pr.pre) > 0.005;
      const dif = ((pr.nota - pr.ref) / pr.ref) * 100;
      return `nota ${brl(pr.nota)} × pré-pedido ${brl(pr.pre)} (+${dif.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%)${mexida ? ' · OC alterada' : ''}`;
    }
    if (p.regra === 'fora_pre_pedido') {
      const pre = /pré-pedido (\d+)/.exec(p.esperado ?? '')?.[1];
      return `está na OC, não no pré-pedido${pre ? ` ${pre}` : ''}${p.mensagem?.includes('incluído na OC depois') ? ' · incluído depois' : ''}`;
    }
    if (p.regra === 'preco' && p.valorUnit != null) {
      const oc = Number(/R\$\s*([\d.,]+)/.exec(p.esperado ?? '')?.[1]?.replace(/\./g, '').replace(',', '.') ?? NaN);
      return `${brl(Number(p.valorUnit))} × OC ${Number.isFinite(oc) ? brl(oc) : (p.esperado ?? '')}`;
    }
    if (p.regra === 'quantidade') return `nota ${p.veio ?? p.quantidade} un. × saldo do pedido ${(p.esperado ?? '').replace(/^até\s*/, '')}`;
    if (p.regra === 'item_fora_oc') {
      // Por extenso: o que é o item, quanto veio, que a OC da nota não tem e qual OC tem.
      const desc = (p.descricao ?? '').replace(/\s+(LOTE|EAN)[:\s].*$/i, '').slice(0, 38);
      return `${desc ? `${desc} · ` : ''}${p.quantidade ?? '?'} un. não está na OC ${nota.pedidoCompra ?? ''}${p.ocSugerida ? ` → está na OC ${p.ocSugerida}` : ''}`;
    }
    return null;
  };
  const d = detalhe();
  return (
    <span className="flex min-w-0 items-center gap-1.5" title={lista.map((x) => `${TIPO_DIVERGENCIA[x.regra] ?? x.regra}${x.cprod ? ` · ${x.cprod}` : ''}`).join('\n')}>
      <span className="shrink-0 rounded bg-status-divergenciaFundo px-1.5 py-0.5 text-2xs font-semibold text-status-divergencia">
        {TIPO_DIVERGENCIA[p!.regra] ?? p!.regra}
      </span>
      <span className="min-w-0 truncate">
        {p!.produto ? <b className="font-mono text-texto">SKU {p!.produto}</b> : p!.cprod ? <b className="font-mono text-texto">{p!.cprod}</b> : null}
        {d ? ` · ${d}` : p!.descricao ? ` · ${p!.descricao}` : ''}
      </span>
      {nota.divergenciasAbertas > 1 ? <span className="shrink-0 text-2xs text-textoSuave">+{nota.divergenciasAbertas - 1}</span> : null}
    </span>
  );
}

/** AAAA-MM-DD de hoje + `dias`, no fuso local. */
function diaIso(dias: number): string {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
